import { useState, useEffect } from 'react'
import { X, MapPin, Route, Mountain, Bookmark, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@cya-routes/shared'
import { getUserBookmarks, toggleBookmark, Bookmark as BookmarkType } from '../../services/firebase/bookmarks'

interface BookmarksModalProps {
  isOpen: boolean
  onClose: () => void
}

export function BookmarksModal({ isOpen, onClose }: BookmarksModalProps) {
  const navigate = useNavigate()
  const { auth } = useAppStore()
  const [bookmarks, setBookmarks] = useState<BookmarkType[]>([])
  const [loading, setLoading] = useState(true)
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [lastDoc, setLastDoc] = useState<any>(null)
  
  const ITEMS_PER_PAGE = 12

  // Load bookmarks
  useEffect(() => {
    if (isOpen && auth.currentUser?.id) {
      loadBookmarks()
    }
  }, [isOpen, auth.currentUser?.id])

  const loadBookmarks = async (loadMore = false) => {
    if (!auth.currentUser?.id) return
    
    setLoading(true)
    try {
      const { bookmarks: newBookmarks, lastDoc: newLastDoc } = await getUserBookmarks(
        auth.currentUser.id,
        ITEMS_PER_PAGE,
        loadMore ? lastDoc : undefined
      )
      
      if (loadMore) {
        setBookmarks(prev => [...prev, ...newBookmarks])
      } else {
        setBookmarks(newBookmarks)
      }
      
      setLastDoc(newLastDoc)
      setHasMore(newBookmarks.length === ITEMS_PER_PAGE)
    } catch (error) {
      console.error('Error loading bookmarks:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveBookmark = async (bookmark: BookmarkType) => {
    if (!auth.currentUser?.id) return
    
    setRemovingIds(prev => new Set(prev).add(bookmark.routeId))
    
    try {
      await toggleBookmark(auth.currentUser.id, {
        routeId: bookmark.routeId,
        routeName: bookmark.routeName,
        routeThumbnail: bookmark.routeThumbnail,
        routeDistance: bookmark.routeDistance,
        routeElevation: bookmark.routeElevation,
        routeLocation: bookmark.routeLocation,
        routeType: bookmark.routeType,
        clubId: bookmark.clubId,
        clubName: bookmark.clubName
      })
      
      // Remove from local state
      setBookmarks(prev => prev.filter(b => b.routeId !== bookmark.routeId))
    } catch (error) {
      console.error('Error removing bookmark:', error)
    } finally {
      setRemovingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(bookmark.routeId)
        return newSet
      })
    }
  }

  const handleRouteClick = (routeId: string) => {
    navigate(`/route/${routeId}`)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-800 border-b border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bookmark className="w-6 h-6 text-emerald-400" />
              <h2 className="text-xl font-bold text-white">My Bookmarked Routes</h2>
              <span className="text-sm text-gray-400">({bookmarks.length} routes)</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          {loading && bookmarks.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Loading bookmarks...</p>
              </div>
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No bookmarked routes yet</h3>
              <p className="text-gray-400 text-sm">
                Start exploring routes and bookmark your favorites to access them here
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="group relative bg-gray-700/50 rounded-lg overflow-hidden hover:bg-gray-700 transition-all duration-200 cursor-pointer"
                    onClick={() => handleRouteClick(bookmark.routeId)}
                  >
                    {/* Image */}
                    <div className="relative h-48 bg-gray-800">
                      {bookmark.routeThumbnail ? (
                        <img
                          src={bookmark.routeThumbnail}
                          alt={bookmark.routeName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Route className="w-12 h-12 text-gray-600" />
                        </div>
                      )}
                      
                      {/* Remove bookmark button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveBookmark(bookmark)
                        }}
                        disabled={removingIds.has(bookmark.routeId)}
                        className="absolute top-2 right-2 p-2 bg-red-600/90 hover:bg-red-700 text-white rounded-full backdrop-blur-sm transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove bookmark"
                      >
                        {removingIds.has(bookmark.routeId) ? (
                          <div className="w-4 h-4 animate-spin">
                            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                              <circle 
                                className="opacity-25" 
                                cx="12" 
                                cy="12" 
                                r="10" 
                                stroke="currentColor" 
                                strokeWidth="4"
                              />
                              <path 
                                className="opacity-75" 
                                fill="currentColor" 
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          </div>
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-2 line-clamp-1 group-hover:text-emerald-400 transition-colors">
                        {bookmark.routeName}
                      </h3>
                      
                      {/* Location */}
                      {bookmark.routeLocation && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
                          <MapPin className="w-3 h-3" />
                          <span>{bookmark.routeLocation}</span>
                        </div>
                      )}
                      
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {bookmark.routeDistance !== undefined && (
                          <div className="flex items-center gap-1">
                            <Route className="w-3 h-3 text-emerald-400" />
                            <span className="text-gray-300">{bookmark.routeDistance.toFixed(1)} km</span>
                          </div>
                        )}
                        {bookmark.routeElevation !== undefined && (
                          <div className="flex items-center gap-1">
                            <Mountain className="w-3 h-3 text-emerald-400" />
                            <span className="text-gray-300">{bookmark.routeElevation.toLocaleString()} m</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Club and Type */}
                      <div className="flex items-center justify-between mt-3">
                        {bookmark.clubName && (
                          <span className="text-xs text-gray-500">{bookmark.clubName}</span>
                        )}
                        {bookmark.routeType && (
                          <span className="text-xs px-2 py-0.5 bg-gray-600 text-gray-300 rounded">
                            {bookmark.routeType}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* View route indicator */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink className="w-4 h-4 text-emerald-400" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Load more button */}
              {hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setPage(prev => prev + 1)
                      loadBookmarks(true)
                    }}
                    disabled={loading}
                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}